import React, { useEffect, useRef, useState } from 'react';
import { animate, useReducedMotion } from 'framer-motion';

interface CountUpProps {
  /** Value to land on. */
  value: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  className?: string;
}

/**
 * Animates a number counting from its previous value to the new one whenever
 * `value` changes (mount included, animating up from 0). Used for the Leads
 * summary stat tiles.
 *
 * Reduced motion: `animate()` is an imperative framer-motion call, so it is
 * NOT covered by the page-level `<MotionConfig reducedMotion="user">` — it
 * checks `useReducedMotion()` itself and snaps straight to the final value.
 */
export function CountUp({ value, decimals = 0, prefix = '', suffix = '', duration = 0.8, className }: CountUpProps) {
  const prefersReducedMotion = useReducedMotion();
  const [display, setDisplay] = useState(prefersReducedMotion ? value : 0);
  const previousValue = useRef(prefersReducedMotion ? value : 0);

  useEffect(() => {
    if (prefersReducedMotion) {
      setDisplay(value);
      previousValue.current = value;
      return;
    }

    const controls = animate(previousValue.current, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (latest) => setDisplay(latest),
    });
    previousValue.current = value;

    return () => controls.stop();
  }, [value, duration, prefersReducedMotion]);

  return (
    <span className={className}>
      {prefix}
      {display.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
}
